/**
 * Client-side downscaling for blog image uploads.
 *
 * Runs before the file leaves the browser, so a 5MB phone photo reaches S3 as a
 * few hundred KB. It also protects the backend's fallback path: when S3 is not
 * configured, upload_blog_image() returns a base64 data URL, and that string is
 * stored inside the post HTML - a full-size photo there bloats every read of
 * the article.
 */

// Wide enough for a full-bleed image on a desktop article, which is the largest
// any blog image is ever displayed at.
export const MAX_UPLOAD_DIMENSION = 1600;

const QUALITY = 0.85;

// Left untouched: SVG is vector, so rasterising it would be a downgrade, and a
// GIF may be animated - a canvas redraw would flatten it to a single frame.
const PASSTHROUGH_TYPES = new Set(['image/svg+xml', 'image/gif']);

// Formats worth preserving on the way out. Anything else becomes JPEG, which is
// the right default for photographs.
const KEEP_FORMAT = new Set(['image/png', 'image/webp']);

async function loadBitmap(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      // 'from-image' applies the EXIF orientation tag. Without it, photos shot
      // in portrait on a phone are drawn to the canvas sideways.
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch (err) {
      // Older Safari rejects the options bag; fall through to the <img> path,
      // where the browser applies orientation itself.
    }
  }

  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Could not decode image'));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas encoding failed'))),
      type,
      quality
    );
  });
}

/**
 * Downscale an image file so neither side exceeds maxDimension.
 *
 * Returns the original File untouched when it is already small enough, is a
 * format we deliberately skip, or when anything goes wrong - the upload should
 * never fail just because the optimisation could not run.
 *
 * @param {File} file
 * @param {{ maxDimension?: number }} [options]
 * @returns {Promise<File>}
 */
export async function downscaleImage(file, { maxDimension = MAX_UPLOAD_DIMENSION } = {}) {
  if (!file || !file.type?.startsWith('image/') || PASSTHROUGH_TYPES.has(file.type)) {
    return file;
  }

  let source;
  try {
    source = await loadBitmap(file);
    const width = source.width;
    const height = source.height;
    if (!width || !height) return file;

    const scale = Math.min(1, maxDimension / Math.max(width, height));
    // Already within bounds. Re-encoding here would only lose quality for no
    // saving, so the original is kept byte for byte.
    if (scale === 1) return file;

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);

    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);

    const outputType = KEEP_FORMAT.has(file.type) ? file.type : 'image/jpeg';
    const blob = await canvasToBlob(canvas, outputType, QUALITY);

    // A resize that grew the file helps nobody - keep whichever is smaller.
    if (blob.size >= file.size) return file;

    // The backend derives an SEO filename from the slug or title, but the
    // extension comes from this name, so it has to match the new encoding.
    const base = file.name.replace(/\.[^.]+$/, '') || 'image';
    const ext = outputType === 'image/jpeg' ? 'jpg' : outputType.split('/')[1];

    return new File([blob], `${base}.${ext}`, {
      type: outputType,
      lastModified: Date.now(),
    });
  } catch (err) {
    // Canvas is unavailable or the file could not be decoded. Upload as-is and
    // let the backend's own size limit be the guard.
    return file;
  } finally {
    if (source && typeof source.close === 'function') source.close();
  }
}

/** Human-readable file size, for the "resized from X to Y" toast. */
export function formatBytes(bytes) {
  if (!Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
